import { useState } from 'react'
import type { PartialUserResponse } from '@kfolks/types'
import { UserRole } from '@kfolks/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { roleAtLeast } from '@/auth'
import { useAuth } from '@/hooks/useAuth'
import { useFilteredQuery } from '@/shared/hooks/useFilteredQuery'
import { FilterBar } from '@/shared/components/molecules/FilterBar'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/atoms/ui/table'
import { Button } from '@/shared/components/atoms/ui/button'
import { Input } from '@/shared/components/atoms/ui/input'
import { Badge } from '@/shared/components/atoms/ui/badge'
import { usersApi } from '../api/users.api'
import { usersKeys } from '../hooks/query-keys'

export function UsersPage() {
	const { user } = useAuth()
	const isSuperAdmin = roleAtLeast(user?.role, UserRole.SUPER_ADMIN)
	const queryClient = useQueryClient()

	const {
		data: users,
		isLoading,
		error,
		tempFilters,
		setTempFilters,
		applyFilters,
		clearFilters,
	} = useFilteredQuery<PartialUserResponse>({
		endpoint: '/users',
		queryKey: 'users',
	})

	const updateMutation = useMutation({
		mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => usersApi.updateUser(id, data),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: usersKeys.users.list() })
		},
	})

	const deleteMutation = useMutation({
		mutationFn: (userId: string) => usersApi.deleteUser(userId),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: usersKeys.users.list() })
		},
	})

	const [editingId, setEditingId] = useState<string | null>(null)
	const [editingData, setEditingData] = useState<{
		displayName: string
		email: string
		role: string
		isActive: boolean
	} | null>(null)

	const handleSaveEdit = async () => {
		if (!editingData || !editingId) return
		await updateMutation.mutateAsync({ id: editingId, data: editingData })
		setEditingId(null)
		setEditingData(null)
	}

	const handleDelete = async (id: string) => {
		if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) return
		await deleteMutation.mutateAsync(id)
	}

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
			</div>
		)
	}

	if (error) return <div className="text-center py-8 text-red-600">Error: {error.message}</div>

	return (
		<div>
			<div className="flex justify-between items-center mb-6">
				<h1 className="text-2xl font-bold text-foreground">Users</h1>
			</div>

			<FilterBar
				filters={[
					{ key: 'displayName', label: 'Name', type: 'text' },
					{ key: 'email', label: 'Email', type: 'text' },
					{ key: 'role', label: 'Role', type: 'text' },
				]}
				values={tempFilters}
				onChange={setTempFilters}
				onApply={applyFilters}
				onClear={clearFilters}
			/>

			<div className="border rounded-lg">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Name</TableHead>
							<TableHead>Email</TableHead>
							<TableHead>Role</TableHead>
							<TableHead>Status</TableHead>
							<TableHead className="text-right">Actions</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{users.map((u) => {
							const isEditing = editingId === u.userId
							return (
								<TableRow key={u.userId}>
									<TableCell>
										{isEditing && editingData ? (
											<Input
												value={editingData.displayName}
												onChange={(e) => setEditingData({ ...editingData, displayName: e.target.value })}
											/>
										) : (
											u.displayName
										)}
									</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{isEditing && editingData ? (
											<Input
												value={editingData.email}
												onChange={(e) => setEditingData({ ...editingData, email: e.target.value })}
											/>
										) : (
											u.email
										)}
									</TableCell>
									<TableCell>
										{isEditing && editingData ? (
											<select
												value={editingData.role}
												onChange={(e) => setEditingData({ ...editingData, role: e.target.value })}
												className="border rounded px-2 py-1 text-sm"
											>
												{Object.values(UserRole).map((role) => (
													<option key={role} value={role}>
														{role}
													</option>
												))}
											</select>
										) : (
											<span className="text-sm text-muted-foreground">{u.role}</span>
										)}
									</TableCell>
									<TableCell>
										{isEditing && editingData ? (
											<label className="flex items-center gap-2 text-sm cursor-pointer">
												<input
													type="checkbox"
													checked={editingData.isActive}
													onChange={(e) => setEditingData({ ...editingData, isActive: e.target.checked })}
													className="rounded"
												/>
												{editingData.isActive ? 'Active' : 'Inactive'}
											</label>
										) : u.isActive ? (
											<Badge>Active</Badge>
										) : (
											<Badge variant="secondary">Inactive</Badge>
										)}
									</TableCell>
									<TableCell className="text-right">
										{isSuperAdmin &&
											(isEditing ? (
												<div className="flex items-center justify-end gap-1">
													<Button
														onClick={() => void handleSaveEdit()}
														size="sm"
														disabled={updateMutation.isPending}
														className="h-7 px-2 text-xs"
													>
														Save
													</Button>
													<Button
														onClick={() => {
															setEditingId(null)
															setEditingData(null)
														}}
														size="sm"
														variant="outline"
														className="h-7 px-2 text-xs"
													>
														Cancel
													</Button>
												</div>
											) : (
												<div className="flex items-center justify-end gap-1">
													<Button
														onClick={() => {
															setEditingId(u.userId ?? null)
															setEditingData({
																displayName: u.displayName ?? '',
																email: u.email ?? '',
																role: u.role ?? 'USER',
																isActive: u.isActive ?? true,
															})
														}}
														size="sm"
														variant="outline"
														className="h-7 px-2 text-xs"
													>
														Edit
													</Button>
													<Button
														onClick={() => u.userId && void handleDelete(u.userId)}
														size="sm"
														variant="destructive"
														disabled={deleteMutation.isPending}
														className="h-7 px-2 text-xs"
													>
														Delete
													</Button>
												</div>
											))}
									</TableCell>
								</TableRow>
							)
						})}
						{users.length === 0 && (
							<TableRow>
								<TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
									No users found.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
		</div>
	)
}
